import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../../../utils';
import { useSnackbar } from '../../../context/SnackbarContext';
import { API_ENDPOINTS } from '../../../constants';

import type { CampaignQuestion } from '../CampaignQuestionsEditor/types';

interface CopyQuestionsResponse {
    success: boolean;
    results: {
        campaignId: number;
        success: boolean;
        error?: string;
    }[];
}

interface CopyQuestionsParams {
    questions: CampaignQuestion[];
    targetCampaignIds: number[];
    sourceCampaignId: number;
}

/**
 * Hook for copying campaign questions to multiple target campaigns
 *
 * @returns {Object} Mutation object with mutate function and status
 */
const useCopyQuestions = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async ({ questions, targetCampaignIds, sourceCampaignId }: CopyQuestionsParams) => {
            const response = await api.post(API_ENDPOINTS.CAMPAIGN_QUESTIONS_COPY, {
                questions,
                targetCampaignIds,
                sourceCampaignId,
            });

            return response.data as CopyQuestionsResponse;
        },
        onSuccess: data => {
            // Invalidate queries for all successfully copied campaigns
            data.results.forEach(result => {
                if (result.success) {
                    queryClient.invalidateQueries({ queryKey: ['api', 'campaign', result.campaignId, 'questions'] });
                    queryClient.invalidateQueries({ queryKey: ['qualityScoreFormula', result.campaignId] });
                }
            });
            // Campaigns list shows avg scores — re-fetch so scores reflect the updated formulas.
            queryClient.invalidateQueries({ queryKey: ['campaigns'] });
        },
        onError: error => {
            showSnackbar(getErrorMessage(error) ?? 'Failed to copy questions', 'error');
        },
    });
};

export default useCopyQuestions;
